'use client';

import React, { useState } from 'react';
import {
    CheckCircle2,
    XCircle,
    Clock,
    AlertCircle,
    ArrowUpRight,
    Search,
    Filter,
    ChevronRight,
    MessageSquare,
    Eye
} from 'lucide-react';

export default function ApprovalPage() {
    const [selectedTab, setSelectedTab] = useState<'semua' | 'pending' | 'selesai'>('pending');
    const [selectedApproval, setSelectedApproval] = useState<any>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const approvals = [
        {
            id: 'APP-001',
            title: 'Pengajuan Dana ATK Semester Genap',
            unit: 'PAUD Unit 1',
            requestedBy: 'Siti Aminah (Admin)',
            amount: 'Rp 2.500.000',
            date: '06 Feb 2026',
            status: 'pending',
            priority: 'normal',
            category: 'Operasional (ATK)',
            description: 'Pembelian kertas, tinta printer, dan alat tulis untuk kegiatan belajar mengajar Semester Genap 2026.'
        },
        {
            id: 'APP-002',
            title: 'Gaji Guru Honorer Januari',
            unit: 'PAUD Unit 1',
            requestedBy: 'Budi (Bendahara)',
            amount: 'Rp 15.000.000',
            date: '05 Feb 2026',
            status: 'pending',
            priority: 'high',
            category: 'Gaji & Tunjangan',
            description: 'Pembayaran gaji untuk 5 orang guru honorer di PAUD Unit 1 untuk periode bulan Januari 2026.'
        },
        {
            id: 'APP-003',
            title: 'Perbaikan Atap Kantin Bocor',
            unit: 'PAUD Unit 2',
            requestedBy: 'Rahmat (Staff)',
            amount: 'Rp 8.000.000',
            date: '04 Feb 2026',
            status: 'approved',
            priority: 'normal',
            category: 'Sarana Prasarana',
            description: 'Perbaikan atap kantin yang mengalami kebocoran di area tengah, perlu segera ditangani sebelum musim hujan memuncak.'
        }
    ];

    const handleOpenDetail = (item: any) => {
        setSelectedApproval(item);
        setIsModalOpen(true);
    };

    const filteredApprovals = approvals.filter(item => {
        if (selectedTab === 'pending') return item.status === 'pending';
        if (selectedTab === 'selesai') return item.status === 'approved' || item.status === 'rejected';
        return true;
    });

    return (
        <div className="p-6 md:p-10 space-y-8 max-w-7xl mx-auto w-full pb-20 animate-in fade-in duration-500">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
                <div className="space-y-1">
                    <h1 className="text-3xl font-black tracking-tight text-foreground">
                        Persetujuan <span className="text-primary">(Approval)</span>
                    </h1>
                    <p className="text-secondary font-bold uppercase tracking-[0.2em] text-[10px]">
                        Operasional • Manajemen Pengajuan Dana
                    </p>
                </div>

                <div className="flex gap-3 bg-surface p-1 rounded-2xl border border-border-main shadow-sm">
                    {(['semua', 'pending', 'selesai'] as const).map((tab) => (
                        <button
                            key={tab}
                            onClick={() => setSelectedTab(tab)}
                            className={`px-6 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${selectedTab === tab
                                ? 'bg-primary text-white shadow-md'
                                : 'text-muted hover:text-primary hover:bg-primary-soft/50'
                                }`}
                        >
                            {tab}
                        </button>
                    ))}
                </div>
            </div>

            {/* Stats Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-surface border border-border-main p-6 rounded-[2rem] flex items-center gap-4 group hover:border-primary/30 transition-all shadow-xs">
                    <div className="w-12 h-12 rounded-2xl bg-warning-soft flex items-center justify-center">
                        <Clock className="w-6 h-6 text-warning" />
                    </div>
                    <div>
                        <p className="text-[10px] font-black text-muted uppercase tracking-widest">Menunggu</p>
                        <p className="text-2xl font-black text-foreground">12 <span className="text-xs font-bold text-muted">Pengajuan</span></p>
                    </div>
                </div>
                <div className="bg-surface border border-border-main p-6 rounded-[2rem] flex items-center gap-4 group hover:border-primary/30 transition-all shadow-xs">
                    <div className="w-12 h-12 rounded-2xl bg-success-soft flex items-center justify-center">
                        <CheckCircle2 className="w-6 h-6 text-success" />
                    </div>
                    <div>
                        <p className="text-[10px] font-black text-muted uppercase tracking-widest">Disetujui</p>
                        <p className="text-2xl font-black text-foreground">145 <span className="text-xs font-bold text-muted">Bulan Ini</span></p>
                    </div>
                </div>
                <div className="bg-surface border border-border-main p-6 rounded-[2rem] flex items-center gap-4 group hover:border-primary/30 transition-all shadow-xs">
                    <div className="w-12 h-12 rounded-2xl bg-error-soft flex items-center justify-center">
                        <XCircle className="w-6 h-6 text-error" />
                    </div>
                    <div>
                        <p className="text-[10px] font-black text-muted uppercase tracking-widest">Ditolak</p>
                        <p className="text-2xl font-black text-foreground">3 <span className="text-xs font-bold text-muted">Bulan Ini</span></p>
                    </div>
                </div>
            </div>

            {/* Main List */}
            <div className="bg-surface border border-border-main rounded-[2.5rem] overflow-hidden shadow-sm">
                <div className="p-8 border-b border-border-main flex flex-col md:flex-row justify-between items-center gap-4">
                    <div className="relative flex-1 max-w-md w-full">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
                        <input
                            type="text"
                            placeholder="Cari ID pengajuan atau nama pengirim..."
                            className="bg-background/50 border border-border-main rounded-xl py-3 pl-11 pr-5 text-xs font-medium focus:ring-4 focus:ring-primary/5 focus:border-primary/30 transition-all outline-none w-full"
                        />
                    </div>
                    <button className="flex items-center gap-2 px-6 py-3 border border-border-main rounded-xl text-[10px] font-black text-muted uppercase tracking-widest hover:bg-primary-soft hover:text-primary transition-all">
                        <Filter className="w-4 h-4" /> Filter Lanjutan
                    </button>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-primary-soft/30">
                                <th className="px-8 py-5 text-[10px] font-black text-muted uppercase tracking-[0.15em] border-b border-border-main">Info Pengajuan</th>
                                <th className="px-8 py-5 text-[10px] font-black text-muted uppercase tracking-[0.15em] border-b border-border-main">Unit & Pemohon</th>
                                <th className="px-8 py-5 text-[10px] font-black text-muted uppercase tracking-[0.15em] border-b border-border-main">Nominal</th>
                                <th className="px-8 py-5 text-[10px] font-black text-muted uppercase tracking-[0.15em] border-b border-border-main">Status</th>
                                <th className="px-8 py-5 text-[10px] font-black text-muted uppercase tracking-[0.15em] border-b border-border-main text-right">Aksi</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border-main/50">
                            {filteredApprovals.map((item) => (
                                <tr key={item.id} className="group hover:bg-primary-soft/20 transition-all">
                                    <td className="px-8 py-6">
                                        <div className="flex flex-col gap-1">
                                            <span className="text-[10px] font-black text-primary mb-1 tracking-widest">{item.id}</span>
                                            <span className="text-sm font-black text-foreground group-hover:text-primary transition-colors">{item.title}</span>
                                            <span className="text-[10px] font-medium text-muted">{item.date}</span>
                                        </div>
                                    </td>
                                    <td className="px-8 py-6">
                                        <div className="flex flex-col gap-1">
                                            <span className="text-xs font-bold text-foreground">{item.unit}</span>
                                            <span className="text-[10px] font-bold text-muted uppercase tracking-wider">{item.requestedBy}</span>
                                        </div>
                                    </td>
                                    <td className="px-8 py-6">
                                        <span className="text-sm font-black text-foreground">{item.amount}</span>
                                    </td>
                                    <td className="px-8 py-6">
                                        <div className="flex items-center gap-2">
                                            {item.priority === 'high' && (
                                                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-error-soft text-error rounded-full animate-pulse">
                                                    <AlertCircle className="w-3 h-3" />
                                                    <span className="text-[9px] font-black uppercase">Urgent</span>
                                                </div>
                                            )}
                                            <div className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-wider ${item.status === 'pending' ? 'bg-warning-soft text-warning' :
                                                item.status === 'approved' ? 'bg-success-soft text-success' : 'bg-error-soft text-error'
                                                }`}>
                                                {item.status}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-8 py-6 text-right">
                                        <div className="flex items-center justify-end gap-2">
                                            <button
                                                onClick={() => handleOpenDetail(item)}
                                                className="p-2.5 bg-white border border-border-main rounded-xl hover:bg-primary-soft hover:text-primary transition-all shadow-sm group/btn"
                                                title="Lihat Detail"
                                            >
                                                <Eye className="w-4 h-4 transition-transform group-hover/btn:scale-110" />
                                            </button>
                                            {item.status === 'pending' && (
                                                <>
                                                    <button className="p-2.5 bg-error-soft text-error rounded-xl hover:bg-error hover:text-white transition-all shadow-sm" title="Tolak">
                                                        <XCircle className="w-4 h-4" />
                                                    </button>
                                                    <button className="p-2.5 bg-primary text-white rounded-xl hover:bg-primary-dark transition-all shadow-lg shadow-primary/20" title="Setujui">
                                                        <CheckCircle2 className="w-4 h-4" />
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="p-8 bg-background/30 border-t border-border-main flex justify-between items-center text-[10px] font-bold text-muted uppercase tracking-widest">
                    <span>Menampilkan {filteredApprovals.length} dari 24 item</span>
                    <div className="flex gap-2">
                        <button className="px-4 py-2 border border-border-main rounded-lg hover:bg-primary-soft">Sebelumnya</button>
                        <button className="px-4 py-2 border border-border-main rounded-lg hover:bg-primary-soft">Selanjutnya</button>
                    </div>
                </div>
            </div>

            {/* Detail Modal */}
            {isModalOpen && selectedApproval && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/40 backdrop-blur-sm animate-in fade-in duration-300">
                    <div
                        className="fixed inset-0"
                        onClick={() => setIsModalOpen(false)}
                    ></div>
                    <div className="bg-surface w-full max-w-2xl rounded-[3rem] shadow-2xl border border-border-main p-10 space-y-10 animate-in zoom-in-95 duration-300 relative z-10 font-sans">
                        {/* Header Modal */}
                        <div className="flex justify-between items-start">
                            <div className="space-y-1">
                                <div className="flex items-center gap-3">
                                    <span className="text-[10px] font-black text-primary tracking-widest uppercase">{selectedApproval.id}</span>
                                    {selectedApproval.priority === 'high' && (
                                        <span className="px-2 py-0.5 bg-error-soft text-error text-[8px] font-black rounded-full uppercase">Urgent</span>
                                    )}
                                </div>
                                <h2 className="text-2xl font-black text-foreground tracking-tight leading-tight">{selectedApproval.title}</h2>
                            </div>
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="p-2.5 hover:bg-error-soft hover:text-error rounded-2xl transition-all"
                            >
                                <XCircle className="w-6 h-6" />
                            </button>
                        </div>

                        {/* Info Grid */}
                        <div className="grid grid-cols-2 gap-8">
                            <div className="space-y-1.5 p-5 rounded-3xl bg-background/50 border border-border-main/50">
                                <p className="text-[9px] font-black text-muted uppercase tracking-[0.1em]">Unit Penempatan</p>
                                <p className="text-sm font-black text-foreground">{selectedApproval.unit}</p>
                            </div>
                            <div className="space-y-1.5 p-5 rounded-3xl bg-background/50 border border-border-main/50">
                                <p className="text-[9px] font-black text-muted uppercase tracking-[0.1em]">Kategori Biaya</p>
                                <p className="text-sm font-black text-foreground">{selectedApproval.category || 'Operasional'}</p>
                            </div>
                            <div className="space-y-1.5 p-5 rounded-3xl bg-background/50 border border-border-main/50">
                                <p className="text-[9px] font-black text-muted uppercase tracking-[0.1em]">Nama Pemohon</p>
                                <p className="text-sm font-black text-foreground">{selectedApproval.requestedBy}</p>
                            </div>
                            <div className="space-y-1.5 p-5 rounded-3xl bg-background/50 border border-border-main/50">
                                <p className="text-[9px] font-black text-muted uppercase tracking-[0.1em]">Tanggal Pengajuan</p>
                                <p className="text-sm font-black text-foreground">{selectedApproval.date}</p>
                            </div>
                            <div className="col-span-2 space-y-2.5 p-6 rounded-3xl bg-primary-soft/30 border border-primary/10">
                                <p className="text-[10px] font-black text-primary uppercase tracking-[0.15em]">Nominal Pengajuan</p>
                                <p className="text-3xl font-black text-primary tracking-tight">{selectedApproval.amount}</p>
                            </div>
                        </div>

                        {/* Description */}
                        <div className="space-y-3">
                            <p className="text-[10px] font-black text-muted uppercase tracking-[0.15em] ml-1 flex items-center gap-2 italic">
                                <MessageSquare className="w-3.5 h-3.5" /> Deskripsi Pengajuan
                            </p>
                            <div className="p-6 rounded-3xl bg-background/50 border border-border-main text-sm font-medium text-foreground/80 leading-relaxed italic">
                                "{selectedApproval.description}"
                            </div>
                        </div>

                        {/* Footer / Actions inside modal */}
                        <div className="pt-6 border-t border-border-main flex gap-4">
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="flex-1 py-4 px-6 border border-border-main rounded-2xl text-[11px] font-black uppercase tracking-widest text-muted hover:bg-muted transition-all"
                            >
                                Kembali
                            </button>
                            {selectedApproval.status === 'pending' && (
                                <>
                                    <button className="flex-1 py-4 px-6 bg-error-soft text-error rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-error hover:text-white transition-all shadow-sm">
                                        Tolak
                                    </button>
                                    <button className="flex-1 py-4 px-6 bg-primary text-white rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-primary-dark transition-all shadow-xl shadow-primary/20">
                                        Setujui Dana
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

